import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, timer, Subscription } from 'rxjs';
import { ImpersonationSessionEntity, MembershipRosterEntity } from 'mj_generatedentities';
import { UserInfo } from '@memberjunction/core';
import { DataFetchService } from './data-fetch.service';
import { ImpersonationPermissionService, PermissionValidationResult } from './impersonation-permission.service';
import { ImpersonationAuditService } from './impersonation-audit.service';
import { EntityNames } from '../utils/constants';
import { UserRoleEntity } from '@memberjunction/core-entities';

/**
 * Active impersonation session data
 */
export interface ImpersonationSession {
  id: string;
  originalUser: UserInfo;
  impersonatedUser: UserInfo;
  startedAt: Date;
  expiresAt: Date;
  reason?: string;
  isActive: boolean;
}

/**
 * Session status for UI indicators
 */
export interface SessionStatus {
  isImpersonating: boolean;
  session?: ImpersonationSession;
  timeRemaining?: number; // in seconds
  warningThreshold?: number; // seconds before expiry to show warning
}

/**
 * Result of session operations
 */
export interface SessionOperationResult {
  success: boolean;
  message: string;
  session?: ImpersonationSession;
  permissionResult?: PermissionValidationResult;
}

/**
 * Main impersonation service that manages sessions with integrated permission checking
 * Handles session lifecycle, expiry, and integrates with audit logging
 */
@Injectable({
  providedIn: 'root'
})
export class ImpersonationService {
  private readonly SESSION_STORAGE_KEY = 'impersonation_session';
  private readonly DEFAULT_SESSION_DURATION = 60 * 60 * 1000; // 1 hour in milliseconds
  private readonly WARNING_THRESHOLD = 5 * 60; // 5 minutes in seconds

  private sessionSubject = new BehaviorSubject<SessionStatus>({ isImpersonating: false });
  private expiryTimer?: Subscription;
  private warningShown = false;
  private expiryHandledSessions = new Set<string>();

  public sessionStatus$ = this.sessionSubject.asObservable();

  constructor(
    private dataFetchService: DataFetchService,
    private permissionService: ImpersonationPermissionService,
    private auditService: ImpersonationAuditService,
    private router: Router
  ) {
    // Initialize service by checking for existing session
    this.initializeService();
  }

  /**
   * Starts an impersonation session after permission validation
   * @param originalUser - Staff user requesting impersonation
   * @param targetUser - User to impersonate
   * @param reason - Optional reason for impersonation
   * @param duration - Session duration in milliseconds (default 1 hour)
   * @returns Promise resolving to operation result
   */
  public async startImpersonation(
    originalUser: UserInfo,
    targetUser: UserInfo,
    reason?: string,
    duration: number = this.DEFAULT_SESSION_DURATION
  ): Promise<SessionOperationResult> {
    try {
      // Validate permissions using target user info
      // Permission service will fetch PersonId from ExtendedUsers/MembershipRoster if needed
      const permissionResult = await this.permissionService.canImpersonate(
        targetUser.ID,        // User_ID - preferred for ExtendedUsers lookup
        targetUser.Email,     // Email - fallback (will check ExtendedUsers, then MembershipRoster)
        undefined             // PersonId - will be fetched internally if needed
      );
      
      if (!permissionResult.isAllowed) {
        return {
          success: false,
          message: `Impersonation denied: ${permissionResult.reason}`,
          permissionResult
        };
      }

      // End any existing session
      await this.endCurrentSession();
      
      // Clear expiry handled sessions for fresh start
      this.expiryHandledSessions.clear();

      // Create session entity
      const sessionEntity = await this.createSessionEntity(originalUser, targetUser, reason, duration);
      if (!sessionEntity) {
        return {
          success: false,
          message: 'Failed to create impersonation session in database'
        };
      }

      // Create session object
      const session: ImpersonationSession = {
        id: sessionEntity.ID!,
        originalUser,
        impersonatedUser: targetUser,
        startedAt: new Date(sessionEntity.StartedAt),
        expiresAt: new Date(sessionEntity.ExpiresAt!),
        reason,
        isActive: true
      };

      // Store in localStorage for persistence across page reloads
      this.storeSession(session);

      // Log session start
      await this.auditService.logSessionStart(
        session.id,
        originalUser,
        targetUser,
        { 
          reason,
          sessionDuration: duration,
          permissions: permissionResult.matchedRule ? [permissionResult.matchedRule.impersonatorRoleName] : undefined
        }
      );

      // Update session status and start timer
      this.updateSessionStatusObservable(session);
      this.startExpiryTimer(session);

      return {
        success: true,
        message: `Successfully started impersonating ${targetUser.Name}`,
        session,
        permissionResult
      };
    } catch (error) {
      console.error('Error starting impersonation:', error);
      return {
        success: false,
        message: `Failed to start impersonation: ${error instanceof Error ? error.message : 'Unknown error'}`
      };
    }
  }

  /**
   * Ends the current impersonation session
   * @param reason - Reason for ending session ('manual', 'expired', 'forced')
   * @returns Promise resolving to operation result
   */
  public async endCurrentSession(reason: 'manual' | 'expired' | 'forced' = 'manual'): Promise<SessionOperationResult> {
    try {
      const currentSession = this.getCurrentSession();
      if (!currentSession) {
        return {
          success: true,
          message: 'No active impersonation session to end'
        };
      }

      // Update session in database
      await this.updateSessionStatus(currentSession.id, 'Ended');

      // Calculate session duration
      const sessionDuration = Date.now() - currentSession.startedAt.getTime();

      // Log session end
      await this.auditService.logSessionEnd(
        currentSession.id,
        currentSession.originalUser,
        currentSession.impersonatedUser,
        {
          reason,
          sessionDuration
        }
      );

      // Clear local storage and timers
      this.clearSession();
      this.stopExpiryTimer();
      
      // Remove from expiry handled sessions when session ends
      if (currentSession) {
        this.expiryHandledSessions.delete(currentSession.id);
      }

      // Update session status
      this.sessionSubject.next({ isImpersonating: false });

      // Navigate to dashboard after session ends
      this.router.navigate(['/dashboard']);

      return {
        success: true,
        message: `Impersonation session ended (${reason})`
      };
    } catch (error) {
      console.error('Error ending impersonation session:', error);
      return {
        success: false,
        message: `Failed to end session: ${error instanceof Error ? error.message : 'Unknown error'}`
      };
    }
  }

  /**
   * Extends the current session by the specified duration
   * @param additionalTime - Additional time in milliseconds
   * @returns Promise resolving to operation result
   */
  public async extendSession(additionalTime: number = this.DEFAULT_SESSION_DURATION): Promise<SessionOperationResult> {
    try {
      const currentSession = this.getCurrentSession();
      if (!currentSession) {
        return {
          success: false,
          message: 'No active session to extend'
        };
      }

      const newExpiryTime = new Date(currentSession.expiresAt.getTime() + additionalTime);

      // Update session in database
      const sessionEntity = await this.dataFetchService.fetchRecordById<ImpersonationSessionEntity>(
        EntityNames.ImpersonationSessions,
        currentSession.id
      );

      if (sessionEntity) {
        sessionEntity.ExpiresAt = newExpiryTime;
        await sessionEntity.Save();
      }

      // Update local session
      currentSession.expiresAt = newExpiryTime;
      this.storeSession(currentSession);

      // Log session extension
      await this.auditService.logSessionExtend(
        currentSession.id,
        currentSession.originalUser,
        currentSession.impersonatedUser,
        additionalTime
      );

      // Restart expiry timer
      this.stopExpiryTimer();
      this.startExpiryTimer(currentSession);
      this.warningShown = false; // Reset warning flag

      // Update session status
      this.updateSessionStatusObservable(currentSession);

      return {
        success: true,
        message: `Session extended by ${Math.round(additionalTime / 60000)} minutes`,
        session: currentSession
      };
    } catch (error) {
      console.error('Error extending session:', error);
      return {
        success: false,
        message: `Failed to extend session: ${error instanceof Error ? error.message : 'Unknown error'}`
      };
    }
  }

  /**
   * Gets the current active session
   * @returns Current session or null if none active
   */
  public getCurrentSession(): ImpersonationSession | null {
    const sessionData = localStorage.getItem(this.SESSION_STORAGE_KEY);
    if (!sessionData) {
      return null;
    }

    try {
      const storedSession = JSON.parse(sessionData);
      
      // Check if session is expired first
      const expiresAt = new Date(storedSession.expiresAt);
      if (expiresAt < new Date()) {
        // Check if we've already handled expiry for this session
        if (!this.expiryHandledSessions.has(storedSession.id)) {
          // Mark as handled first to prevent repeated processing
          this.expiryHandledSessions.add(storedSession.id);
          
          // Create a minimal session for expiry handling
          const expiredSession: ImpersonationSession = {
            id: storedSession.id,
            originalUser: { ID: storedSession.originalUser.ID } as UserInfo,
            impersonatedUser: { ID: storedSession.impersonatedUser.ID } as UserInfo,
            startedAt: new Date(storedSession.startedAt),
            expiresAt: expiresAt,
            reason: storedSession.reason,
            isActive: false
          };
          this.handleSessionExpiry(expiredSession);
        }
        return null;
      }

      // For active sessions, reconstruct with basic UserInfo objects
      // Note: UserRoles will need to be fetched separately by components that need them
      const session: ImpersonationSession = {
        id: storedSession.id,
        originalUser: {
          ID: storedSession.originalUser.ID,
          Name: storedSession.originalUser.Name,
          Email: storedSession.originalUser.Email
        } as UserInfo,
        impersonatedUser: {
          ID: storedSession.impersonatedUser.ID,
          Name: storedSession.impersonatedUser.Name,
          Email: storedSession.impersonatedUser.Email
        } as UserInfo,
        startedAt: new Date(storedSession.startedAt),
        expiresAt: expiresAt,
        reason: storedSession.reason,
        isActive: storedSession.isActive
      };

      return session;
    } catch (error) {
      console.error('Error parsing session data:', error);
      this.clearSession();
      return null;
    }
  }

  /**
   * Checks if currently impersonating
   * @returns True if actively impersonating
   */
  public isImpersonating(): boolean {
    return this.getCurrentSession() !== null;
  }

  /**
   * Gets current session status for UI binding
   * @returns Observable of session status
   */
  public getSessionStatus(): Observable<SessionStatus> {
    return this.sessionStatus$;
  }

  /**
   * Gets the full UserInfo with roles for the impersonated user
   * This fetches the complete user data from the database
   * @returns Promise resolving to UserInfo with roles, or null if no active session
   */
  public async getImpersonatedUserWithRoles(): Promise<UserInfo | null> {
    const session = this.getCurrentSession();
    if (!session) {
      return null;
    }

    try {
      // Fetch full user data including roles
      const userResult = await this.dataFetchService.fetchWithParams<UserInfo>({
        EntityName: EntityNames.Users,
        ExtraFilter: `ID = '${session.impersonatedUser.ID}'`,
        skipActiveFilter: true,
        ResultType: 'simple'
      });

      if (userResult.success && userResult.data.length > 0) {
        const userInfo = userResult.data[0];

        // Load user roles separately and merge them
        const userRoleResult = await this.dataFetchService.fetchWithParams<UserRoleEntity>({
          EntityName: EntityNames.UserRoles,
          ExtraFilter: `UserID = '${session.impersonatedUser.ID}'`,
          skipActiveFilter: true,
          ResultType: 'simple'
        });

        // Create enriched user info with roles, ensuring all critical fields are populated
        const enrichedUserInfo: UserInfo = Object.assign({}, userInfo, {
          // Core identification fields
          ID: userInfo.ID,
          Name: userInfo.Name,
          Email: userInfo.Email,
          
          // Name fields with fallbacks
          FirstName: userInfo.FirstName || userInfo.Name?.split(' ')[0] || 'User',
          LastName: userInfo.LastName || userInfo.Name?.split(' ').slice(1).join(' ') || '',
          FirstLast: userInfo.FirstLast || userInfo.Name || 'Unknown User',
          
          // Account info with defaults
          Type: userInfo.Type || 'User',
          IsActive: userInfo.IsActive !== undefined ? userInfo.IsActive : true,
          
          // User roles
          UserRoles: userRoleResult.success && userRoleResult.data.length > 0 
            ? userRoleResult.data.map((ur: any) => ({
                UserID: ur.UserID,
                RoleID: ur.RoleID,
                Role: ur.Role
              }))
            : []
        });

        return enrichedUserInfo;
      }
    } catch (error) {
      console.error('Error fetching impersonated user with roles:', error);
    }

    return null;
  }

  /**
   * Forces termination of a session by admin
   * @param sessionId - Session to terminate
   * @param adminUser - Admin performing the termination
   * @returns Promise resolving to operation result
   */
  public async forceEndSession(sessionId: string, adminUser: UserInfo): Promise<SessionOperationResult> {
    try {
      const sessionEntity = await this.dataFetchService.fetchRecordById<ImpersonationSessionEntity>(
        EntityNames.ImpersonationSessions,
        sessionId
      );

      if (!sessionEntity || sessionEntity.Status !== 'Active') {
        return {
          success: false,
          message: 'Session not found or not active'
        };
      }

      // Update session status
      sessionEntity.Status = 'Ended';
      sessionEntity.EndedAt = new Date();
      await sessionEntity.Save();

      // Log forced termination
      const originalUser = { ID: sessionEntity.OriginalUser_ID } as UserInfo; // Simplified for logging
      const impersonatedUser = { ID: sessionEntity.ImpersonatedUser_ID } as UserInfo;

      await this.auditService.logForceEnd(sessionId, originalUser, impersonatedUser, adminUser);

      return {
        success: true,
        message: 'Session forcibly terminated by administrator'
      };
    } catch (error) {
      console.error('Error forcing session end:', error);
      return {
        success: false,
        message: `Failed to force end session: ${error instanceof Error ? error.message : 'Unknown error'}`
      };
    }
  }

  /**
   * Gets all active impersonation sessions for admin monitoring
   * @returns Promise resolving to array of active sessions
   */
  public async getActiveSessions(): Promise<ImpersonationSession[]> {
    try {
      const now = new Date();
      const filter = `Status = 'Active' AND ExpiresAt > '${now.toISOString()}'`;

      // Use parallel queries to get sessions and users
      const [sessionsResult, usersResult] = await this.dataFetchService.fetchMultipleEntities([
        {
          EntityName: EntityNames.ImpersonationSessions,
          ExtraFilter: filter,
          OrderBy: 'StartedAt DESC',
          ResultType: 'entity_object'
        },
        {
          EntityName: EntityNames.Users,
          ResultType: 'entity_object'
        }
      ]);

      if (!sessionsResult.success || !usersResult.success) {
        console.error('Error fetching active sessions or users');
        return [];
      }

      const sessions = sessionsResult.data as ImpersonationSessionEntity[];
      const users = usersResult.data as any[];
      
      // Create user lookup map for performance
      const userMap = new Map();
      users.forEach(user => userMap.set(user.ID, user.Name));

      return sessions.map(session => ({
        id: session.ID!,
        originalUser: { 
          ID: session.OriginalUser_ID, 
          Name: userMap.get(session.OriginalUser_ID) || 'Unknown User' 
        } as UserInfo,
        impersonatedUser: { 
          ID: session.ImpersonatedUser_ID, 
          Name: userMap.get(session.ImpersonatedUser_ID) || 'Unknown User' 
        } as UserInfo,
        startedAt: new Date(session.StartedAt),
        expiresAt: new Date(session.ExpiresAt!),
        reason: session.Reason || undefined,
        isActive: true
      }));
    } catch (error) {
      console.error('Error getting active sessions:', error);
      return [];
    }
  }

  /**
   * Initializes the service on startup
   */
  private initializeService(): void {
    const currentSession = this.getCurrentSession();
    if (currentSession) {
      this.updateSessionStatusObservable(currentSession);
      this.startExpiryTimer(currentSession);
    }
  }

  /**
   * Creates a new impersonation session entity in database
   */
  private async createSessionEntity(
    originalUser: UserInfo,
    targetUser: UserInfo,
    reason: string | undefined,
    duration: number
  ): Promise<ImpersonationSessionEntity | null> {
    try {
      const entity = await this.dataFetchService.createEntityInstance<ImpersonationSessionEntity>(
        EntityNames.ImpersonationSessions
      );

      if (!entity) {
        throw new Error('Failed to create session entity');
      }

      const now = new Date();
      const expiresAt = new Date(now.getTime() + duration);

      entity.OriginalUser_ID = originalUser.ID;
      entity.ImpersonatedUser_ID = targetUser.ID;
      entity.StartedAt = now;
      entity.ExpiresAt = expiresAt;
      entity.Status = 'Active';
      entity.Reason = reason || null;

      const success = await entity.Save();
      if (!success) {
        throw new Error('Failed to save session entity');
      }

      return entity;
    } catch (error) {
      console.error('Error creating session entity:', error);
      return null;
    }
  }

  /**
   * Updates session status in database
   */
  private async updateSessionStatus(sessionId: string, status: string): Promise<void> {
    try {
      const entity = await this.dataFetchService.fetchRecordById<ImpersonationSessionEntity>(
        EntityNames.ImpersonationSessions,
        sessionId
      );

      if (entity) {
        entity.Status = status;
        if (status === 'Ended' || status === 'Expired') {
          entity.EndedAt = new Date();
        }
        await entity.Save();
      }
    } catch (error) {
      console.error('Error updating session status:', error);
    }
  }

  /**
   * Stores session in localStorage with only essential data
   */
  private storeSession(session: ImpersonationSession): void {
    // Store only essential data to avoid quota issues
    // UserInfo objects can be large with circular references
    const sessionData = {
      id: session.id,
      originalUser: {
        ID: session.originalUser.ID,
        Name: session.originalUser.Name,
        Email: session.originalUser.Email
      },
      impersonatedUser: {
        ID: session.impersonatedUser.ID,
        Name: session.impersonatedUser.Name,
        Email: session.impersonatedUser.Email
      },
      startedAt: session.startedAt,
      expiresAt: session.expiresAt,
      reason: session.reason,
      isActive: session.isActive
    };
    
    try {
      localStorage.setItem(this.SESSION_STORAGE_KEY, JSON.stringify(sessionData));
    } catch (error) {
      // If localStorage is full, try to clear some space and retry
      console.error('Failed to store session in localStorage:', error);
      if (error instanceof DOMException && error.name === 'QuotaExceededError') {
        // Clear old/expired sessions if any
        this.clearOldStorageData();
        // Try once more
        localStorage.setItem(this.SESSION_STORAGE_KEY, JSON.stringify(sessionData));
      }
    }
  }

  /**
   * Clears session from localStorage
   */
  private clearSession(): void {
    localStorage.removeItem(this.SESSION_STORAGE_KEY);
  }

  /**
   * Clears old/unnecessary data from localStorage to free up space
   */
  private clearOldStorageData(): void {
    try {
      // Remove any expired session data
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.includes('impersonation') && key !== this.SESSION_STORAGE_KEY) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));
    } catch (error) {
      console.error('Error clearing old storage data:', error);
    }
  }

  /**
   * Updates the session status observable
   */
  private updateSessionStatusObservable(session: ImpersonationSession): void {
    const timeRemaining = Math.max(0, (session.expiresAt.getTime() - Date.now()) / 1000);
    
    this.sessionSubject.next({
      isImpersonating: true,
      session,
      timeRemaining,
      warningThreshold: this.WARNING_THRESHOLD
    });
  }

  /**
   * Starts the expiry timer for session monitoring
   */
  private startExpiryTimer(session: ImpersonationSession): void {
    const updateInterval = 30000; // Update every 30 seconds
    
    this.expiryTimer = timer(0, updateInterval).subscribe(() => {
      const now = Date.now();
      const timeRemaining = (session.expiresAt.getTime() - now) / 1000;
      
      if (timeRemaining <= 0) {
        // Session expired
        this.handleSessionExpiry(session);
        return;
      }

      // Show warning if within threshold and not already shown
      if (timeRemaining <= this.WARNING_THRESHOLD && !this.warningShown) {
        this.showExpiryWarning(timeRemaining);
        this.warningShown = true;
      }

      // Update status
      this.updateSessionStatusObservable(session);
    });
  }

  /**
   * Stops the expiry timer
   */
  private stopExpiryTimer(): void {
    if (this.expiryTimer) {
      this.expiryTimer.unsubscribe();
      this.expiryTimer = undefined;
    }
  }

  /**
   * Handles session expiry
   */
  private async handleSessionExpiry(session: ImpersonationSession): Promise<void> {
    
    // Clear the expired session from localStorage immediately to prevent repeated checks
    this.clearSession();
    
    // Log expiry - but only once per session due to our expiry handling logic
    const sessionDuration = Date.now() - session.startedAt.getTime();
    await this.auditService.logSessionExpire(
      session.id,
      session.originalUser,
      session.impersonatedUser,
      sessionDuration
    );

    // End session (this will try to clear localStorage again, but that's safe)
    await this.endCurrentSession('expired');
  }

  /**
   * Shows expiry warning to user
   */
  private showExpiryWarning(timeRemaining: number): void {
    const minutes = Math.ceil(timeRemaining / 60);
    console.warn(`Impersonation session will expire in ${minutes} minute(s)`);

    // This could trigger a UI notification/modal
    // For now, just log the warning
  }

}